/**
 * Date-scoped loading, caching, and optimistic updates for personalized unread
 * report counts.
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { clinicalTrialsService } from "../api/clinicalTrialsService";
import { toFrontendUnreadCounts } from "../api/transformers";
import type { DocumentType, UnreadDocumentCounts } from "../types";
import { adjustUnreadDocumentCount } from "../utils/notificationReadCache";
import {
  getCachedUnreadDocumentCounts,
  setCachedUnreadDocumentCounts,
} from "../utils/notificationPageState";

interface UnreadDocumentCountState {
  counts: UnreadDocumentCounts;
  decrement: (documentType: DocumentType) => boolean;
  restore: (documentType: DocumentType) => void;
  revertRestore: (documentType: DocumentType) => void;
  revalidate: () => Promise<void>;
}

/**
 * Manage authoritative and optimistic unread report counts for one date.
 *
 * @param selectedDate - Notification roll-up date
 * @returns Count state plus decrement, restore, and revalidation operations
 */
export function useUnreadDocumentCounts(
  selectedDate: string,
): UnreadDocumentCountState {
  const [counts, setCounts] = useState<UnreadDocumentCounts>(
    () => getCachedUnreadDocumentCounts(selectedDate) ?? {},
  );

  useEffect(() => {
    const controller = new AbortController();
    setCounts(getCachedUnreadDocumentCounts(selectedDate) ?? {});
    clinicalTrialsService
      .getTabCounts(selectedDate, controller.signal)
      .then((response) => {
        const nextCounts = toFrontendUnreadCounts(response.counts);
        setCachedUnreadDocumentCounts(selectedDate, nextCounts);
        setCounts(nextCounts);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        toast.error(
          err?.message || "Failed to load unread report counts",
          "Unread counts unavailable",
        );
      });
    return () => controller.abort();
  }, [selectedDate]);

  /**
   * Optimistically decrement a loaded category count.
   *
   * @param documentType - Frontend category for the opened report
   * @returns Whether an authoritative count was loaded and adjusted
   */
  const decrement = useCallback(
    (documentType: DocumentType): boolean => {
      if (counts[documentType] === undefined) return false;
      const nextCounts = adjustUnreadDocumentCount(counts, documentType, -1);
      setCachedUnreadDocumentCounts(selectedDate, nextCounts);
      setCounts(nextCounts);
      return true;
    },
    [counts, selectedDate],
  );

  /**
   * Restore one failed optimistic decrement.
   *
   * @param documentType - Frontend category whose mark-read request failed
   * @returns Nothing
   */
  const restore = useCallback(
    (documentType: DocumentType): void => {
      const currentCounts =
        getCachedUnreadDocumentCounts(selectedDate) ?? counts;
      const nextCounts = adjustUnreadDocumentCount(
        currentCounts,
        documentType,
        1,
      );
      setCachedUnreadDocumentCounts(selectedDate, nextCounts);
      setCounts(nextCounts);
    },
    [counts, selectedDate],
  );

  /**
   * Undo one optimistic `restore` (+1) — the exact mirror of `restore`.
   *
   * Cache-based (reads the counts `restore` just wrote) and unconditional, unlike
   * `decrement` which no-ops on an undefined count and recomputes from a stale closure.
   * Used to roll back a failed mark-unread so the badge is never left inflated.
   * `adjustUnreadDocumentCount` clamps at zero, so reverting a 0→1 bump lands back at 0.
   *
   * @param documentType - Frontend category whose mark-unread request failed
   * @returns Nothing
   */
  const revertRestore = useCallback(
    (documentType: DocumentType): void => {
      const currentCounts =
        getCachedUnreadDocumentCounts(selectedDate) ?? counts;
      const nextCounts = adjustUnreadDocumentCount(
        currentCounts,
        documentType,
        -1,
      );
      setCachedUnreadDocumentCounts(selectedDate, nextCounts);
      setCounts(nextCounts);
    },
    [counts, selectedDate],
  );

  /**
   * Refresh cached counts after a successful mark-read operation.
   *
   * @returns Promise resolved after the best-effort refresh
   */
  const revalidate = useCallback(async (): Promise<void> => {
    try {
      const response = await clinicalTrialsService.getTabCounts(selectedDate);
      const nextCounts = toFrontendUnreadCounts(response.counts);
      setCachedUnreadDocumentCounts(selectedDate, nextCounts);
      setCounts(nextCounts);
    } catch {
      // The optimistic decrement is already correct after a successful mark.
    }
  }, [selectedDate]);

  return { counts, decrement, restore, revertRestore, revalidate };
}
